import type { DeepReadonly, Ref } from "vue";
import { isEqual } from "lodash-es";
import type { QueryParamsScheme } from "./types/QueryParamsScheme";
import type {
  QueryParams,
  QueryScheme2RefValObject,
  QueryScheme2ValObject,
} from "~/modules/helpers/composables/useQueryParams/types";

export function useQueryParams<T extends QueryParamsScheme = QueryParamsScheme>(
  scheme: T,
) {
  const router = useRouter();
  const route = useRoute();

  const queryVariables = reactive<QueryScheme2ValObject<T>>(
    parse() as QueryScheme2ValObject<T>,
  );
  const committedQueryVariables = ref(parseToRawObject());

  // todo remove this
  // setInterval(() => {
  //   console.log("--------------------");
  //   console.log("scheme", scheme);
  //   console.log("queryVariables", queryVariables, queryVariables.page);
  //   console.log("committedQueryVariables", committedQueryVariables);
  // }, 5000);

  async function commitPush(): Promise<void> {
    await router.push({
      path: route.path,
      query: prepareToCommit(),
    });
  }

  async function commitReplace(): Promise<void> {
    await router.replace({
      path: route.path,
      query: prepareToCommit(),
    });
  }

  function parse(): QueryScheme2RefValObject<T> {
    const val: Partial<QueryScheme2RefValObject<T>> = {};

    Object.entries(scheme).forEach((entry) => {
      const key: keyof T = entry[0];
      const value = entry[1];
      value.parse(route.query[key.toString()]);
      val[key] = value.getValue();
    });

    return val as QueryScheme2RefValObject<T>;
  }

  function parseToRawObject(): QueryScheme2ValObject<T> {
    const val: Partial<QueryScheme2RefValObject<T>> = {};

    Object.entries(scheme).forEach((entry) => {
      const key: keyof T = entry[0];
      const value = entry[1];
      value.parse(route.query[key.toString()]);
      val[key] = value.getUnrefValue();
    });

    return val as QueryScheme2ValObject<T>;
  }

  function prepareToCommit(): QueryParams {
    return Object.fromEntries(
      Object.entries(queryVariables)
        .filter(([key, val]) => {
          if (!scheme[key]) {
            throw new Error(`${key} is not exist in scheme`);
          }
          return !scheme[key].isDefault(val);
        })
        .map(([k]) => {
          return [k, scheme[k].toRaw()];
        })
        .filter(([_, val]) => !!val),
    );
  }

  async function reset(): Promise<void> {
    Object.values(scheme).forEach((v) => v.reset());
    await commitPush();
  }

  watch(
    () => route.query,
    () => {
      const newRawParsed = parseToRawObject();
      if (!isEqual(newRawParsed, committedQueryVariables)) {
        committedQueryVariables.value = ref(newRawParsed).value;
      }
    },
    { deep: true },
  );

  return {
    queryVariables: queryVariables as QueryScheme2ValObject<T>,
    committedQueryVariables: committedQueryVariables as DeepReadonly<
      Ref<QueryScheme2ValObject<T>>
    >,
    commitPush,
    commitReplace,
    reset,
  };
}
